import { useState } from 'react';
import { motion, AnimatePresence, springs, Skeleton } from '../lib/motion';
import { haptic } from '../lib/haptics';

/**
 * Simple combobox: type-ahead select with "create new" affordance.
 * - results: array of { id, label }
 * - onSelect: callback when an existing item is picked
 * - onCreate: callback when user wants to create a brand-new item with the typed text
 */
export interface ComboboxItem { id: string; label: string; sublabel?: string }
export function Combobox({
  placeholder, value, onChange, results, onSelect, onCreate, createLabel = 'Add',
  loading, emptyLabel, autoOpen = false, mobileInlineResults = false,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  results: ComboboxItem[];
  onSelect: (item: ComboboxItem) => void;
  onCreate?: () => void;
  createLabel?: string;
  loading?: boolean;
  emptyLabel?: string;
  autoOpen?: boolean;
  mobileInlineResults?: boolean;
}) {
  const [open, setOpen] = useState(autoOpen);
  const showCreate = Boolean(onCreate) && value.trim().length >= 2 &&
    !results.some((r) => r.label.toLowerCase() === value.trim().toLowerCase());
  const hasContent = results.length > 0 || showCreate || loading;
  const showDropdown = open && (hasContent || (emptyLabel && !loading && results.length === 0));
  return (
    <div className="relative">
      <div className="relative">
        <svg
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input
          value={value}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={Boolean(showDropdown)}
          aria-autocomplete="list"
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={(e) => {
            setOpen(true);
            if (mobileInlineResults) {
              setTimeout(() => e.currentTarget.scrollIntoView({ block: 'start', behavior: 'smooth' }), 80);
            }
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="w-full bg-white border border-slate-200 rounded-2xl pl-11 pr-4 py-3.5 text-base sm:py-3 sm:text-sm text-slate-800 placeholder-slate-400 shadow-card focus:outline-none focus:border-squishy-teal focus:shadow-glow transition-shadow"
        />
      </div>
      <AnimatePresence>
        {showDropdown && (
          <motion.ul
            initial={{ opacity: 0, y: -6, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.99 }}
            transition={springs.snappy}
            role="listbox"
            className={[
              'z-10 mt-2 w-full overflow-auto bg-white border border-slate-100 rounded-2xl shadow-pop text-sm overscroll-contain divide-y divide-slate-100',
              mobileInlineResults
                ? 'relative max-h-[min(44vh,18rem)] sm:absolute sm:max-h-60'
                : 'absolute max-h-60',
            ].join(' ')}
          >
            {loading && results.length === 0 && (
              <li className="px-4 py-3.5 space-y-2.5" aria-hidden>
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3.5 w-1/2" />
                <Skeleton className="h-3.5 w-2/3" />
              </li>
            )}
            {!loading && results.length === 0 && !showCreate && emptyLabel && (
              <li className="px-4 py-3.5 text-slate-400">{emptyLabel}</li>
            )}
            {results.map((r) => (
              <motion.li
                key={r.id}
                role="option"
                aria-selected={false}
                onMouseDown={() => { haptic.selection(); onSelect(r); }}
                whileTap={{ scale: 0.985 }}
                transition={springs.snappy}
                className="px-4 py-3.5 sm:py-3 min-h-[44px] hover:bg-slate-50 active:bg-teal-50 cursor-pointer transition-colors"
              >
                <div className="font-medium text-slate-800">{r.label}</div>
                {r.sublabel && <div className="text-xs text-slate-500 mt-0.5">{r.sublabel}</div>}
              </motion.li>
            ))}
            {showCreate && (
              <motion.li
                onMouseDown={() => { haptic.light(); onCreate?.(); }}
                whileTap={{ scale: 0.985 }}
                transition={springs.snappy}
                className="px-4 py-3.5 sm:py-3 min-h-[44px] hover:bg-teal-50 active:bg-teal-50 cursor-pointer transition-colors text-squishy-tealDark font-medium"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </span>
                  <span>{createLabel} <strong>"{value.trim()}"</strong></span>
                </span>
              </motion.li>
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
